// Import Libraries
import React, { useEffect, useContext, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronRight,
  faChevronCircleUp,
  faChevronCircleDown,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import io from 'socket.io-client';
import { Box } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';

// Import Components
import ShotTable from './shotTable';
import PdfViewer from '../../components/Fragments/PDFviewer.js';
import ProFlag from '../../components/Elements/ProFlag.js';
import Icon from '../../components/Elements/Icon.js';
import SceneGenerationSection from '../../components/Fragments/Project/sceneGenerateSection.js';
import ScenesToolbar from '../../components/Fragments/ProjectHeader/ScenesToolbar.js';
import headerTheme from '../../components/Fragments/ProjectHeader/headerTheme.js';

// Import Services
import ScriptService from '../../services/scriptService.js';
import Utils from '../../services/utilities.js';
import { computeScenePermissions, canMoveScene } from '../../hooks/useScenePermissions.js';
import analyticsService from '../../services/analyticsService.js';

// Context
import ScriptContext from '../../context/Script.js';
import SessionContext from '../../context/Session';
import ProjectViewContext from '../../context/ProjectView';
import scriptService from '../../services/scriptService.js';

const LocationFields = ({ scene, isReadOnly, lockedFields, updateScene, updateSceneText }) => {
  const containerRef = useRef(null);
  const textareaRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width < 400);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (isNarrow) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    } else {
      el.style.height = '';
    }
  }, [scene.header_text, isNarrow]);

  return (
    <span
      ref={containerRef}
      style={
        isNarrow
          ? {
              flex: 16,
              display: 'grid',
              gridTemplateColumns: '1fr 2fr',
              columnGap: '8px',
              rowGap: '4px',
              minWidth: 0,
            }
          : { flex: 16, display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }
      }
    >
      <input
        type="text"
        value={scene.scene_int_ext}
        onChange={(e) => updateScene(scene.id, 'scene_int_ext', e.target.value)}
        placeholder="INT/EXT"
        id={`scene-${scene.id}-scene_int_ext`}
        className={
          isReadOnly || lockedFields.has(`scene-${scene.id}-scene_int_ext`) ? 'readOnly' : ''
        }
        readOnly={isReadOnly || lockedFields.has(`scene-${scene.id}-scene_int_ext`)}
        style={
          isNarrow
            ? {
                margin: 0,
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                gridColumn: 1,
                gridRow: 1,
              }
            : { flex: 2, margin: 0, width: 'auto', minWidth: 0, maxWidth: '70px' }
        }
      />
      <textarea
        ref={textareaRef}
        rows={1}
        value={scene.header_text}
        onChange={(e) => {
          updateSceneText(scene.id, 'header_text', e.target.value);
          if (isNarrow) {
            e.target.style.height = 'auto';
            e.target.style.height = e.target.scrollHeight + 'px';
          } else {
            e.target.style.height = '';
          }
        }}
        onBlur={(e) => updateScene(scene.id, 'header_text', e.target.value)}
        placeholder="Location"
        id={`scene-${scene.id}-header_text`}
        className={
          isReadOnly || lockedFields.has(`scene-${scene.id}-header_text`) ? 'readOnly' : ''
        }
        readOnly={isReadOnly || lockedFields.has(`scene-${scene.id}-header_text`)}
        style={
          isNarrow
            ? {
                resize: 'none',
                overflow: 'hidden',
                margin: 0,
                width: '100%',
                boxSizing: 'border-box',
                gridColumn: '1 / 3',
                gridRow: 2,
              }
            : { flex: 10, resize: 'none', overflow: 'hidden', margin: 0 }
        }
      />
      <input
        type="text"
        value={scene.scene_time_of_day}
        onChange={(e) => updateScene(scene.id, 'scene_time_of_day', e.target.value)}
        placeholder="Time"
        id={`scene-${scene.id}-scene_time_of_day`}
        className={
          isReadOnly || lockedFields.has(`scene-${scene.id}-scene_time_of_day`) ? 'readOnly' : ''
        }
        readOnly={isReadOnly || lockedFields.has(`scene-${scene.id}-scene_time_of_day`)}
        style={
          isNarrow
            ? {
                margin: 0,
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                gridColumn: 2,
                gridRow: 1,
              }
            : { flex: 2, margin: 0, width: 'auto', minWidth: 0, maxWidth: '70px' }
        }
      />
    </span>
  );
};

// Construct Component
const SceneTable = () => {
  const { script, setScript, updateScriptScene } = useContext(ScriptContext);
  const { isMobile, generalModalDetails, setGeneralModalDetails } = useContext(SessionContext);
  const { view, setView, lockedFields, setLockedFields } = useContext(ProjectViewContext);
  const prevSceneCountRef = useRef(0);
  const scenesInitializedRef = useRef(false);

  function changePdfView() {
    setView({ ...view, pdfActive: !view.pdfActive });
  }

  const expandScene = (sceneNumber) => {
    let index =
      sceneNumber === null
        ? null
        : script.scenes.findIndex((scene) => scene.id === sceneNumber) || 0;

    if (view.sceneIndex === index && !isMobile) {
      return;
    }

    if (index || isMobile) {
      let scene = script.scenes[index];
      updateScene(scene.id);
      analyticsService.trackEvent('scene_viewed', {
        scene_index: scene.scene_position,
        is_locked: computeScenePermissions(scene, script).isSceneLocked,
      });
      let sceneImagesAllowed = true;

      if (script.features && script.features.toLowerCase() === 'basic' && scene.ai_status < 1) {
        sceneImagesAllowed = false;
      }

      let onScreen = 'scene';
      if (isMobile) {
        onScreen = 'shot';
      }

      setView((prevView) => {
        return {
          ...prevView,
          sceneIndex: index,
          sceneImagesAllowed: sceneImagesAllowed,
          onScreen: onScreen,
        };
      });
    } else {
      setView((prevView) => {
        return {
          ...prevView,
          sceneIndex: index,
        };
      });
    }
  };

  const updateScene = async (sceneId, field, newValue) => {
    const updatedScenes = script.scenes.map((scene) => {
      if (scene.id === sceneId) {
        let updatedScene = {
          ...scene,
          shot_list: Utils.buildTempShot(script, { ...scene, [field]: newValue }),
        };
        if (field) {
          updatedScene[field] = newValue;
        }
        return updatedScene;
      }
      return scene;
    });

    updateScriptScene(updatedScenes);
    if (!field && !newValue) return;
    try {
      await ScriptService.changeSceneChange(sceneId, field, newValue, script.id);
    } catch (error) {
      console.error('Error updating scene:', error);
    }
  };

  const updateSceneText = (sceneId, field, value) => {
    setScript((prevScript) => {
      const updatedScenes = prevScript.scenes.map((scene) => {
        if (scene.id === sceneId) {
          return {
            ...scene,
            [field]: value,
          };
        }
        return scene;
      });
      return { ...prevScript, scenes: updatedScenes };
    });
  };

  const deleteHandler = (scene) => {
    setGeneralModalDetails({
      open: true,
      header: (
        <>
          <span style={{ paddingRight: '20px', color: '#E35640' }}>
            <Icon
              icon="Trash"
              color="Orange"
              customStyle={{ width: '20px', marginBottom: '-6px' }}
            />
          </span>
          <span>Delete Scene</span>
        </>
      ),
      message: (
        <div>
          Are you sure you want to delete this scene? This will remove all associated shots. This
          action cannot be undone.
          <br />
          <br />
          <div className="fullWidthContainer">
            <button
              className="buttonGray OneHalfFlex"
              onClick={() =>
                setGeneralModalDetails({
                  open: false,
                  header: null,
                  message: null,
                })
              }
            >
              Cancel
            </button>
            <button
              className="OneHalfFlex"
              style={{ backgroundColor: '#E35640', color: '#fff' }}
              onClick={() => deleteScene(scene)}
            >
              Delete Scene
            </button>
          </div>
        </div>
      ),
    });
  };

  const addScene = async () => {
    try {
      const position = script.scenes.length + 1;
      const newScene = await ScriptService.createManualScene(script.id, position);
    } catch (error) {
      console.error('Error adding scene:', error);
    }
  };

  // Viewers and hard-paywalled free scripts can't edit, so they can't reorder either.
  const canReorderScenes =
    script.level !== 'viewer' &&
    ((script.features || '').toLowerCase() === 'pro' || !script.freemium_hard_paywall) &&
    (script.scenes?.length || 0) > 1;

  // Points view.sceneIndex (an index into script.scenes) at wherever the selected
  // scene sits in `scenes`, so the same scene stays open after a reorder.
  const followSelectedScene = (selectedId, scenes) => {
    if (selectedId === undefined) return;
    const newIndex = scenes.findIndex((scene) => scene.id === selectedId);
    if (newIndex !== -1) {
      setView((prevView) => ({ ...prevView, sceneIndex: newIndex }));
    }
  };

  const handleSceneDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    if (!canMoveScene(script, source.index, destination.index)) {
      toast.info('Upgrade to Pro to move scenes in or out of the first five.');
      return;
    }

    const previousIds = script.scenes.map((scene) => scene.id);
    const orderedIds = [...previousIds];
    const [movedId] = orderedIds.splice(source.index, 1);
    orderedIds.splice(destination.index, 0, movedId);

    const selectedId =
      view.sceneIndex === null ? undefined : script.scenes[view.sceneIndex]?.id;

    setScript((prevScript) => ({
      ...prevScript,
      scenes: Utils.applySceneOrder(prevScript.scenes, orderedIds),
    }));
    followSelectedScene(selectedId, Utils.applySceneOrder(script.scenes, orderedIds));

    try {
      await ScriptService.reorderScenes(script.id, orderedIds);
      analyticsService.trackEvent('scene_reordered', {
        from_position: source.index + 1,
        to_position: destination.index + 1,
      });
    } catch (error) {
      console.error('Error reordering scenes:', error);
      setScript((prevScript) => ({
        ...prevScript,
        scenes: Utils.applySceneOrder(prevScript.scenes, previousIds),
      }));
      followSelectedScene(selectedId, Utils.applySceneOrder(script.scenes, previousIds));
      toast.error("We couldn't save the new scene order. Please try again.");
    }
  };

  const deleteScene = async (sceneId) => {
    try {
      setGeneralModalDetails({
        open: false,
        header: null,
        message: null,
      });
      const deleteResponse = await ScriptService.deleteScene(sceneId);
      if (deleteResponse) {
        if (script.scenes.length === 1) {
          setScript((prevScript) => ({ ...prevScript, scenes: [] }));
          await addScene();
          return;
        } else {
          const deletedSceneIndex = script.scenes.findIndex((scene) => scene.id === sceneId);
          const newSceneIndex = deletedSceneIndex > 0 ? deletedSceneIndex - 1 : 0;

          updateScriptScene(script.scenes.filter((scene) => scene.id !== sceneId));

          setView((prevView) => ({ ...prevView, sceneIndex: newSceneIndex }));
        }
      }
    } catch (error) {
      console.error('Error deleting scene:', error);
    }
  };

  useEffect(() => {
    if (view.sceneIndex === null) return;
    if (view.sceneIndex === script.scenes.length - 1) {
      const container = document.getElementById('scrollSceneTable');
      if (container) {
        container.scrollTop = container.scrollHeight + 200;
      }
    }
  }, [view.sceneIndex]);

  useEffect(() => {
    if (!script?.scenes?.length) return;
    updateScene(script.scenes[0].id);
    if (!isMobile) {
      expandScene(script.scenes[0].id);
    }
  }, []);

  // Scroll a newly added scene into view instead of letting it silently
  // land off-screen at the bottom of a long scene list. Skips the initial
  // load (and script switches), which also grow scenes.length from 0 but
  // aren't a user "add scene" action.
  useEffect(() => {
    scenesInitializedRef.current = false;
    prevSceneCountRef.current = 0;
  }, [script.id]);

  useEffect(() => {
    const scenes = script.scenes || [];
    const prevCount = prevSceneCountRef.current;

    if (!scenesInitializedRef.current) {
      if (scenes.length > 0) {
        scenesInitializedRef.current = true;
      }
    } else if (scenes.length > prevCount) {
      const newestScene = scenes[scenes.length - 1];
      const el = document.getElementById(`scene-card-${newestScene.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    prevSceneCountRef.current = scenes.length;
  }, [script.scenes]);

  return (
    <div
      className={`${isMobile ? 'shotTable' : 'ShotList hiddenScroll limitHeightScene'}`}
      style={{
        backgroundColor: '#F9FAFC',
      }}
    >
      {/* Above phone width this toolbar lives in ProjectHeader's second row. */}
      {isMobile && (
        <ThemeProvider theme={headerTheme}>
          <Box sx={{ px: 2, py: 1, bgcolor: '#FAFAFA' }}>
            <ScenesToolbar />
          </Box>
        </ThemeProvider>
      )}
      {script.scenes && script.scenes.length === 0 ? (
        <p>No scenes available. Please ensure your script has scene numbers.</p>
      ) : (
        <DragDropContext onDragEnd={handleSceneDragEnd}>
          <Droppable droppableId="scene-list" direction="vertical">
            {(droppableProvided) => (
              <div
                id="scrollSceneTable"
                style={{ paddingLeft: '20px' }}
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {script.scenes.map((scene, index) => {
                  const { isReadOnly, isGenerationLocked } = computeScenePermissions(scene, script);
                  // Scene text/metadata is authoring, not a premium feature — only
                  // isSceneLocked (the 5-scene freemium cap) should gate shot
                  // generation and the shot list itself, not the raw scene content.
                  // A hard-paywalled script (3rd+ free upload) still blocks editing
                  // entirely, same as before.
                  const locked = isReadOnly || isGenerationLocked;
                  return (
                    <Draggable
                      key={scene.id}
                      draggableId={`scene-${scene.id}`}
                      index={index}
                      isDragDisabled={!canReorderScenes}
                    >
                      {(draggableProvided) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          id={`scene-card-${scene.id}`}
                          className={`card ${
                            script.scenes[view.sceneIndex]?.id === scene.id &&
                            !isMobile &&
                            view.viewColumns == 2
                              ? 'selected'
                              : ''
                          }`}
                          onClick={() => {
                            if (view.viewColumns > 1 || isMobile) {
                              expandScene(scene.id);
                            }
                          }}
                        >
                          <div
                            className={`cardHeader ${scene.shot_list ? 'hasShots' : ''}`}
                            style={{ minHeight: '40px' }}
                          >
                            <div className="fullWidthContainer bold">
                              {/* Dedicated grip rather than the whole card: in 1-column view the
                                  card contains the shot list, whose shots are draggable too. */}
                              {canReorderScenes && (
                                <span
                                  {...draggableProvided.dragHandleProps}
                                  aria-label="Drag to reorder scene"
                                  title="Drag to reorder"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flex: '0 0 auto',
                                    marginLeft: '-12px',
                                    color: '#CCCCCC',
                                    cursor: 'grab',
                                  }}
                                >
                                  <DragIndicatorIcon sx={{ fontSize: 18 }} />
                                </span>
                              )}
                              <span className="TwoPortionFlex cardTop left">
                                <input
                                  type="text"
                                  onChange={(e) => updateScene(scene.id, 'scene_number', e.target.value)}
                                  value={scene.scene_number}
                                  placeholder="#"
                                  id={`scene-${scene.id}-scene_number`}
                                  className={`${
                                    locked || lockedFields.has(`scene-${scene.id}-scene_number`)
                                      ? 'readOnly'
                                      : ''
                                  }`}
                                  readOnly={locked || lockedFields.has(`scene-${scene.id}-scene_number`)}
                                />
                              </span>
                              <span className="TwoPortionFlex cardTop">
                                {(() => {
                                  const portions =
                                    scene.scene_length !== null && scene.scene_length !== undefined
                                      ? scriptService.calculatePortion(scene)
                                      : scene.portions;
                                  if (!portions) return null;
                                  return (
                                    <>
                                      {portions.wholes > 0 && <>{portions.wholes} </>}
                                      {portions.remainder > 0 && (
                                        <>
                                          <span className="numerator">{portions.remainder}</span>/
                                          <span className="denominator">8</span>
                                        </>
                                      )}
                                      {portions.wholes === 0 && portions.remainder === 0 && <>0</>}
                                    </>
                                  );
                                })()}
                              </span>
                              <LocationFields
                                scene={scene}
                                isReadOnly={locked}
                                lockedFields={lockedFields}
                                updateScene={updateScene}
                                updateSceneText={updateSceneText}
                              />
                              <span
                                className={`${isMobile ? 'fullWidthContainer' : 'FourPortionFlex'}`}
                                style={{
                                  display: 'flex',
                                  ...(view.viewColumns == 2
                                    ? { minWidth: '150px', marginRight: '-10px' }
                                    : {}),
                                }}
                              >
                                {isMobile ? (
                                  <>
                                    <span className="ThreePortionFlex">&nbsp;</span>
                                    <span className="EightPortionFlex">
                                      <SceneGenerationSection scene={scene} view={view} setView={setView} />
                                    </span>
                                  </>
                                ) : (
                                  <SceneGenerationSection scene={scene} view={view} setView={setView} />
                                )}

                                <div className={`OnePortionFlex Padded`} style={{ float: 'right' }}>
                                  {isMobile ? (
                                    <FontAwesomeIcon
                                      icon={faChevronRight}
                                      className={`sizeUp linkText`}
                                      onClick={() => {
                                        expandScene(scene.id);
                                      }}
                                      style={{ float: 'right', marginTop: '5px', paddingRight: '5px' }}
                                    />
                                  ) : view.viewColumns == 1 ? (
                                    <>
                                      {scene.shot_list && (
                                        <FontAwesomeIcon
                                          icon={
                                            script.scenes[view.sceneIndex]?.id === scene.id
                                              ? faChevronCircleUp
                                              : faChevronCircleDown
                                          }
                                          style={{ float: 'right' }}
                                          className={`linkText sizeUp`}
                                          onClick={() =>
                                            expandScene(
                                              script.scenes[view.sceneIndex]?.id === scene.id
                                                ? null
                                                : scene.id,
                                            )
                                          }
                                        />
                                      )}
                                    </>
                                  ) : null}
                                </div>
                              </span>
                            </div>
                          </div>

                          {(view.viewColumns == 2 || isMobile) &&
                            script.scenes[view.sceneIndex]?.id == scene.id &&
                            view.uploadedFile &&
                            scene.page_start != null &&
                            scene.page_end != null && (
                              <span
                                className={`OnePortionFlex Padded MinSmall ${
                                  view.pdfActive && script.scenes[view.sceneIndex].id === scene.id
                                    ? ''
                                    : 'hidden'
                                }`}
                              >
                                <PdfViewer
                                  uploadedFile={view.uploadedFile}
                                  styleDetail={`${script.scenes[view.sceneIndex] ? '' : 'pdfContainer1'}`}
                                  startPage={scene.page_start}
                                  endPage={scene.page_end}
                                />
                              </span>
                            )}

                          {!view.uploadedFile ||
                          view.manualEntry ||
                          scene.page_start == null ||
                          scene.page_end == null ? (
                            <div className={`FullWidthFlex Padded`} onClick={() => changePdfView()}>
                              <textarea
                                rows={5}
                                onChange={(e) => {
                                  updateSceneText(scene.id, 'scene_text', e.target.value);
                                  e.target.style.height = 'auto';
                                  const maxHeight = 200;
                                  if (e.target.scrollHeight <= maxHeight) {
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                  } else {
                                    e.target.style.height = maxHeight + 'px';
                                  }
                                }}
                                onBlur={(e) => {
                                  updateScene(scene.id, 'scene_text', e.target.value);
                                  e.target.style.height = 'auto';
                                  const maxHeight = 200;
                                  if (e.target.scrollHeight <= maxHeight) {
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                  } else {
                                    e.target.style.height = maxHeight + 'px';
                                  }
                                }}
                                value={scene.scene_text}
                                placeholder="Copy/paste or type your scene here. Or copy/paste any text, like news articles, prose, and even poetry."
                                id={`scene-${scene.id}-scene_text`}
                                className={`${
                                  locked || lockedFields.has(`scene-${scene.id}-scene_text`) ? 'readOnly' : ''
                                }`}
                                readOnly={locked || lockedFields.has(`scene-${scene.id}-scene_text`)}
                              />
                            </div>
                          ) : null}

                          {view.viewColumns === 1 &&
                          script.scenes[view.sceneIndex]?.id === scene.id &&
                          !isMobile ? (
                            <div
                              className={`${script.scenes[view.sceneIndex].id === scene.id ? '' : 'hidden'}`}
                            >
                              <ShotTable view={view} setView={setView} />
                            </div>
                          ) : null}

                          <div className={`FullWidthFlex Padded`}>
                            <div className="FourPortionFlex">&nbsp;</div>
                            {view.manualEntry ? (
                              <div className={`OnePortionFlex`} style={{ flexDirection: 'row-reverse' }}>
                                <FontAwesomeIcon
                                  className={`linkText orangeText`}
                                  style={{ padding: '1px', float: 'right' }}
                                  onClick={() => deleteHandler(scene.id)}
                                  icon={faTrashCan}
                                />
                              </div>
                            ) : (
                              <div className="OnePortionFlex">&nbsp;</div>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default SceneTable;
